import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {
    convertStartEndDatesForFile,
    sortCurrency,
    sortNumber,
    toCurrency,
} from '../shared/functions';
import {filterCaseInsensitive, getTableState, setDefaultTableSort} from '../shared/tableFunctions';
import ReactTable from 'react-table';
import {CSVLink} from 'react-csv';
import '../shared/react-table.css';
import {commonStyles} from '../shared/styles';
import {tableStyles} from '../shared/styles-table';
import FlatButton from "material-ui/FlatButton";
import DetailIcon from 'material-ui/svg-icons/image/remove-red-eye';
import ExportIcon from 'material-ui/svg-icons/navigation/arrow-downward';
import TableIcon from 'material-ui/svg-icons/communication/location-on';

/**
 * Class for displaying list of Combined Sales Locations in a single table
 *
 */
class CombinedSalesReportLocationsTable extends Component {
    constructor(props) {
        super(props);
        this.state = {
            summaryList: [],
            summaryTableData: [],
            summaryTableHeaders: [
                {label: 'ID', key: 'location_id'},
                {label: 'Lot', key: 'lot_notes'},
                {label: 'Name', key: 'location_name'},
                {label: 'State', key: 'state'},
                {label: '# Adj. Sales', key: 'totalSales'},
                {label: 'Adj. Net', key: 'salesAdjusted'},
                {label: 'Avg. Net', key: 'salesAverage'}
            ],
            message: null,
            summaryComplete: false,
        };
        this.handleExportClick = this.handleExportClick.bind(this);
    }

    componentWillMount() {
        this.processData(this.props.listData);
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.listData !== this.props.listData) {
            this.setState({
                summaryList: [],
                summaryComplete: false,
            });
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.listData !== this.props.listData) {
            this.processData(this.props.listData);
        }
    }

    /* Data Processing ------------------------------ */

    /**
     * Arrange data for use by table
     *
     * @param listData
     */
    processData(listData) {

        const emptyCurrency = toCurrency(0);
        let summaryList = [];

        if (listData) {
            let listLength = listData.length;
            for (let i = 0; i < listLength; i++) {
                let item = listData[i];
                const row = {
                    location_id: (item.location_id) ? item.location_id : '',
                    location_name: (item.location_name) ? item.location_name : '',
                    lot_notes: (item.lot_notes) ? item.lot_notes : '',
                    state: (item.state) ? item.state : '',
                    totalSales: (item.totalSales) ? item.totalSales : 0,
                    salesAdjusted: (item.salesAdjusted) ? (toCurrency(item.salesAdjusted / 100)) : emptyCurrency,
                    salesAverage: (item.salesAverage) ? (toCurrency(item.salesAverage / 100)) : emptyCurrency
                };
                summaryList.push(row);
            }
        }

        // Finalize table data
        this.setState({
            summaryList: summaryList,
            summaryTableData: summaryList,
            summaryComplete: true,
        });
    }

    /* Table Building ------------------------------ */

    /**
     * Update selected table data based on sort
     *
     * @param event
     * @param table
     */
    handleSortChange = (event, table) => {
        const tableData = getTableState(table);
        this.setState({
            summaryTableData: tableData
        });
    };

    /**
     * Update selected table data based on filter
     *
     * @param event
     * @param table
     */
    handleFilterChange = (event, table) => {
        const tableData = getTableState(table);
        this.setState({
            summaryTableData: tableData
        });
    };

    /**
     * Handle event to retrieve current state of table data
     *
     * @param event
     */
    handleExportClick = (event) => {
        // CSVLink handles generation and export of CSV
    };

    /* Table Display ------------------------------ */

    render() {

        const {
            allowLocationSelection,
            dataIsFiltered,
            endDate,
            locationIconFunction,
            startDate,
            system,
        } = this.props;

        const {
            summaryList,
            summaryTableData,
            summaryTableHeaders,
            summaryComplete,
        } = this.state;

        const dateString = convertStartEndDatesForFile(startDate, endDate);
        const defaultSort = setDefaultTableSort('location_id');
        const isFilteredString = (dataIsFiltered) ? '-filtered' : '';
        const iconStyle = (allowLocationSelection) ? tableStyles.tableIconButton : tableStyles.disabledTableIconButton;

        // ReactTable headers and columns for Table
        const tableColumns = [
            {
                Header: 'Location ID',
                accessor: 'location_id',
                sortMethod: (a, b) => sortNumber(a, b, 'ASC'),
                Cell: row => (
                    <span>
                        <span>{row.row.location_id}</span>
                        <DetailIcon
                            onClick={(e) => locationIconFunction(e, row.row)}
                            style={iconStyle}
                        />
                    </span>
                ),
                style: tableStyles.right,
            },
            {
                Header: 'Lot',
                accessor: 'lot_notes',
            },
            {
                Header: 'Name',
                accessor: 'location_name',
            },
            {
                Header: 'State',
                accessor: 'state',
                style: tableStyles.tdMax,
            },
            {
                Header: () => (<span># Adj. Sales</span>),
                accessor: 'totalSales',
                filterable: false,
                style: tableStyles.right
            },
            {
                Header: 'Adj. Net',
                accessor: 'salesAdjusted',
                sortMethod: (a, b) => sortCurrency(a, b),
                filterable: false,
                style: tableStyles.right,
            },
            {
                Header: 'Avg. Net',
                accessor: 'salesAverage',
                sortMethod: (a, b) => sortCurrency(a, b),
                filterable: false,
                style: tableStyles.right,
            }
        ];

        return (
            <div>
                {summaryComplete &&
                <div className="reports-table-container">

                    {/* Table Header: Locations */}
                    <div className="reports-table-title table-table-header">
                        <div className="btn-report-container table-header-button-container">
                            {/* Export Combined Sales Locations */}
                            <FlatButton
                                primary
                                type="button"
                                label=""
                                style={commonStyles.headerButton}
                            >
                                <ExportIcon style={commonStyles.headerButtonIcon}/>
                                <CSVLink
                                    data={summaryTableData}
                                    headers={summaryTableHeaders}
                                    filename={'Combined-Sales-Locations-' + system + '-' + dateString + isFilteredString + '.csv'}
                                    onClick={(e) => this.handleExportClick(e)}
                                    target="_blank"
                                    style={commonStyles.headerButtonLink}
                                >
                                    Export
                                </CSVLink>
                            </FlatButton>
                        </div>
                        <h3 className="table-table-header-title">
                            <TableIcon style={commonStyles.summaryIconSmall}/>
                            <span>By Location</span>
                        </h3>
                    </div>

                    {/* Table: Combined Sales Locations */}
                    <ReactTable
                        key="table-combined-sales-locations"
                        data={summaryList}
                        columns={tableColumns}
                        defaultPageSize={10000}
                        showPagination={false}
                        defaultSorted={defaultSort}
                        filterable
                        defaultFilterMethod={filterCaseInsensitive}
                        onSortedChange={(e) => this.handleSortChange(e, this.locationsReactTable)}
                        onFilteredChange={(e) => this.handleFilterChange(e, this.locationsReactTable)}
                        ref={(r) => this.locationsReactTable = r}
                        resizable={false}
                        minRows={1}
                        className="-striped overflow single-row no-print"
                    />
                    <ReactTable
                        key="table-combined-sales-locations-print"
                        data={summaryTableData}
                        columns={tableColumns}
                        defaultPageSize={10000}
                        showPagination={false}
                        resizable={false}
                        minRows={1}
                        className="print-only"
                    />
                </div>
                }
            </div>
        )
    }
}

CombinedSalesReportLocationsTable.propTypes = {
    addField: PropTypes.bool,
    allowLocationSelection: PropTypes.bool,
    dataIsFiltered: PropTypes.bool,
    endDate: PropTypes.string,
    listData: PropTypes.array,
    locationIconFunction: PropTypes.func,
    startDate: PropTypes.string,
    system: PropTypes.string,
};

CombinedSalesReportLocationsTable.defaultProps = {
    addField: true,
    allowLocationSelection: true,
    dataIsFiltered: false,
    endDate: null,
    startDate: null,
};

export default CombinedSalesReportLocationsTable;



// WEBPACK FOOTER //
// ./src/Fuse/components/CombinedSalesReportLocationsTable.js